/**
 * The board's arrows: each link between two items shown, a straight line from one item's edge
 * to the other's, with its look from `link.style` (`stroke`, `line`: solid/dashed/dotted,
 * `head`: filled/none) and its label in the middle. Drawn in board units inside the layer.
 * A wide clear line under each one takes the clicks (`data-arrow`: its index).
 */
import { For, Show, type JSX } from "solid-js";
import type { Link } from "@protocol/Link";
import { useApp } from "../../../app/context";
import { edgePoint } from "../../../state/board";
import { dashesOf } from "./look";
import { ARROW_HEAD_PX } from "./constants";

/** Whether two links are the same one. */
export const sameArrow = (a: Link, b: Link) => a.from === b.from && a.to === b.to && a.kind === b.kind && (a.pin ?? null) === (b.pin ?? null);

/** Every arrow of the board; `only`: those between these items (a picture of part of the board). */
export function Arrows(props: { only?: ReadonlySet<string> }): JSX.Element {
  const { board, view } = useApp();
  const shown = () => (props.only ? board.arrows().filter((l) => props.only!.has(l.from) && props.only!.has(l.to)) : board.arrows());
  /** The selected link, if one is. */
  const chosen = () => (view.state.selection?.type === "link" ? view.state.selection.link : null);
  return (
    <svg class="board-arrows" aria-hidden="true">
      <For each={shown()}>
        {(link, index) => {
          const ends = () => {
            const places = board.content().places;
            const a = places.get(link.from);
            const b = places.get(link.to);
            if (!a || !b) return null;
            const ca = { x: a.x + a.w / 2, y: a.y + a.h / 2 };
            const cb = { x: b.x + b.w / 2, y: b.y + b.h / 2 };
            return { from: edgePoint(a, cb), to: edgePoint(b, ca) };
          };
          const colour = () => link.style?.stroke || "var(--fg)";
          const selected = () => !!chosen() && sameArrow(chosen()!, link);
          return (
            <Show when={ends()}>
              {(e) => {
                const head = () => {
                  const { from, to } = e();
                  const angle = Math.atan2(to.y - from.y, to.x - from.x);
                  const at = (turn: number) => `${to.x - ARROW_HEAD_PX * Math.cos(angle + turn)},${to.y - ARROW_HEAD_PX * Math.sin(angle + turn)}`;
                  return `${to.x},${to.y} ${at(0.45)} ${at(-0.45)}`;
                };
                return (
                  <g classList={{ "board-arrow": true, selected: selected() }} data-arrow={index()} data-arrow-from={link.from} data-arrow-to={link.to}>
                    <line class="board-arrow-hit" x1={e().from.x} y1={e().from.y} x2={e().to.x} y2={e().to.y} />
                    <line x1={e().from.x} y1={e().from.y} x2={e().to.x} y2={e().to.y} stroke={colour()} stroke-width={selected() ? 3.5 : 2} stroke-dasharray={dashesOf(link.style?.line)} stroke-linecap="round" />
                    <Show when={link.style?.head !== "none"}>
                      <polygon points={head()} fill={colour()} />
                    </Show>
                    <Show when={link.label}>
                      <text x={(e().from.x + e().to.x) / 2} y={(e().from.y + e().to.y) / 2 - 6} text-anchor="middle" class="board-arrow-label">
                        {link.label}
                      </text>
                    </Show>
                  </g>
                );
              }}
            </Show>
          );
        }}
      </For>
    </svg>
  );
}
